import { useCallback, useEffect, useRef } from 'react';

const SESSION_TIMEOUT_MS = 3 * 60 * 60 * 1000; // 3 hours
const LOGIN_TIME_KEY = 'cr_login_time';

/**
 * Encapsulates the session expiration logic: keeps a timer that fires `onExpire`
 * when the current login window passes, and re-checks on tab visibility change.
 *
 * Returns helpers:
 *   - `start()`       — call after a successful login to (re)start the timer
 *   - `clear()`       — call on logout to clear the persisted login time
 *   - `isExpired()`   — synchronous check, true when persisted login is too old
 */
export function useSessionTimer(onExpire) {
  const timeoutRef = useRef(null);
  const loginTimeRef = useRef(null);
  const onExpireRef = useRef(onExpire);
  useEffect(() => { onExpireRef.current = onExpire; }, [onExpire]);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const start = useCallback(() => {
    clearTimer();
    const loginTime =
      loginTimeRef.current ||
      parseInt(localStorage.getItem(LOGIN_TIME_KEY) || '0', 10);
    if (!loginTime) return;
    loginTimeRef.current = loginTime;
    const remaining = SESSION_TIMEOUT_MS - (Date.now() - loginTime);
    if (remaining <= 0) {
      onExpireRef.current?.();
      return;
    }
    timeoutRef.current = setTimeout(() => onExpireRef.current?.(), remaining);
  }, [clearTimer]);

  const persistLogin = useCallback(() => {
    const now = Date.now();
    loginTimeRef.current = now;
    localStorage.setItem(LOGIN_TIME_KEY, String(now));
    start();
  }, [start]);

  const clear = useCallback(() => {
    loginTimeRef.current = null;
    localStorage.removeItem(LOGIN_TIME_KEY);
    clearTimer();
  }, [clearTimer]);

  const isExpired = useCallback(() => {
    const loginTime = parseInt(localStorage.getItem(LOGIN_TIME_KEY) || '0', 10);
    return !!(loginTime && Date.now() - loginTime > SESSION_TIMEOUT_MS);
  }, []);

  // Re-check on tab visibility (catches sleep / background → foreground)
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible' && isExpired()) {
        onExpireRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [isExpired]);

  return { start, clear, isExpired, persistLogin };
}
